import {
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Chip,
    Spinner,
    Tooltip,
    Switch,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { Building, Envelope, EnvelopeSimple } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { RentalContract } from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import ellaIcon from "screens/kaylee/ellaAIKayleeUp.png";

export interface RecentlyCompletedOutgoingTenantsProps { }

const RecentlyCompletedOutgoingTenants = ({ }: RecentlyCompletedOutgoingTenantsProps) => {
    const [contracts, setContracts] = useState<RentalContract[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { session } = useSession();

    const fetchRecentlyCompletedTenants = async () => {
        try {
            setIsLoading(true);
            console.log("Fetching recently completed outgoing tenants...");

            const response = await fetchWithAuth(
                `${API_URL}/rental/recently-completed-outgoing-tenants`,
                { method: "GET" },
                session
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch recently completed outgoing tenants: ${response.status}`);
            }

            const data = await response.json();
            setContracts(data);
        } catch (error) {
            console.error("Failed to fetch recently completed outgoing tenants:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRecentlyCompletedTenants();
    }, []);

    const formatDateDDMMYYYY = (date: Date) => {
        return new Date(date).toLocaleDateString('nl-BE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <div className="flex h-48 items-center justify-center">
                <Spinner
                    label="Gegevens aan het ophalen"
                    color="success"
                    size="md"
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col rounded-lg bg-white shadow-sm">
            <Table
                aria-label="Recently completed outgoing tenants"
                classNames={{
                    wrapper: "shadow-none rounded-lg p-0",
                    thead: "h-fit border-b-2 border-neutral-primary/40",
                    th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                    tbody: "border-b divide-y divide-neutral-primary/15",
                    tr: "divide-x divide-neutral-primary/15",
                    td: "py-2.5",
                }}
            >
                <TableHeader>
                    <TableColumn>Unit</TableColumn>
                    <TableColumn>Huurders</TableColumn>
                    <TableColumn>Checklist</TableColumn>
                    <TableColumn>Uitgaande plaatsbeschrijving</TableColumn>
                    <TableColumn>Ella's agenda</TableColumn>
                    <TableColumn>Bezoekdagen</TableColumn>
                    <TableColumn>Verantwoordelijke</TableColumn>
                    <TableColumn>Waarborg</TableColumn>
                    <TableColumn>Ingehouden waarborg</TableColumn>
                    <TableColumn>Vrijgegeven</TableColumn>
                    <TableColumn>Status</TableColumn>
                </TableHeader>
                <TableBody>
                    {contracts.map((contract) => (
                        <TableRow key={contract.id}>
                            <TableCell>
                                <Chip
                                    startContent={<Building weight="duotone" size={14} />}
                                    classNames={{
                                        base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                        content: "px-0 font-medium",
                                    }}
                                >
                                    {contract.unit?.id}
                                </Chip>
                            </TableCell>
                            <TableCell>
                                {contract.contractContacts
                                    ?.map((contact: any) =>
                                        `${contact.relatedContact?.firstName} ${contact.relatedContact?.lastName}`
                                    )
                                    .join(", ")}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    {contract.outgoingInspectionChecklistSent ? (
                                        <Tooltip content="Checklist verzonden">
                                            <div className="text-success">
                                                <EnvelopeSimple size={20} />
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip content="Checklist niet verzonden">
                                            <div className="text-neutral-300">
                                                <Envelope size={20} />
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                {contract.outgoingInspectionDate && formatDateDDMMYYYY(contract.outgoingInspectionDate)}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <img
                                        src={ellaIcon}
                                        alt="Ella's agenda"
                                        className={`w-5 h-5 ${
                                            contract.outgoingInspectionCalendarEventId
                                                ? 'opacity-100'
                                                : 'opacity-50'
                                        }`}
                                    />
                                </div>
                            </TableCell>
                            <TableCell>{contract.visitationDays}</TableCell>
                            <TableCell>{contract.visitationResponsible}</TableCell>
                            <TableCell>
                                {applyMask(contract.guarantee?.[0]?.amount, "valuta") || "N/A"}
                            </TableCell>
                            <TableCell>
                                {applyMask(contract.guaranteeAmountToWithhold, "valuta")}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        isSelected={contract.guaranteeReleased}
                                        isDisabled={true}
                                        size="sm"
                                        color="success"
                                    >
                                        {contract.guaranteeReleased ? "Vrijgegeven" : "Niet vrijgegeven"}
                                    </Switch>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    classNames={{
                                        base: "bg-success/20 text-success border-success/30",
                                        content: "text-sm font-medium",
                                    }}
                                >
                                    Afgerond
                                </Chip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default RecentlyCompletedOutgoingTenants; 